/**
 * What a stuck run should TELL its operator.
 *
 * WHY THIS EXISTS. On 2026-09-09 an operator watched a real run on
 * analytics/daily-check-report and reported: "the UI gives absolutely no help in solving
 * this or guiding the operator. There is no recovery even if it is a failure and guidance
 * to fix it." Both of their runs that day were failures the BACKEND had already learned to
 * diagnose, and the rail rendered neither diagnosis:
 *
 *   - one sat 28 minutes in `results` showing "Working on results…", with no elapsed time,
 *     no budget, and no hint that `results` reverts each changed file and re-runs the whole
 *     suite once per file — so minutes are expected, and a person cannot tell expected from
 *     wedged;
 *   - the other burned 16 attempts against TWO tests that failed identically nine times
 *     while the passing count climbed 506 -> 511 -> 517. The run ended with "stopped after
 *     16 attempt(s): the phase used its full attempt budget and every attempt was refused",
 *     which points at the implementation when the tests were the suspect.
 *
 * A verdict the backend produces and the UI does not surface is a diagnosis nobody receives.
 * These helpers turn run state into the sentence an operator can act on, and they are pure
 * so they can be tested without a fleet.
 */
import type { AuthoringRunState, Phase } from './AuthoringRail'

export function runStoppedBy(run: AuthoringRunState | null): { by: string; phase: string; cancelled: boolean } | null {
  const checkpoint = run?.checkpoints?.find(c => c.decision === 'stop')
  if (!checkpoint) return null
  return {
    by: checkpoint.decided_by?.trim() || 'an operator',
    phase: checkpoint.at_phase,
    cancelled: checkpoint.reason === 'operator-cancelled',
  }
}

/** A turn is hard-bounded by copilotJobRunTimeout (2460s). Mirrored, not guessed. */
export const TURN_BUDGET_SECONDS = 2460

/**
 * What each phase is actually doing, for the phases where "working…" is not enough.
 *
 * ONLY THE EXPENSIVE AND THE SURPRISING. A caption on every phase would be noise an operator
 * learns to skip, and the one that matters would go with it.
 */
export function phaseActivityNote(phase: Phase): string | null {
  if (phase === 'results') {
    return 'Reverting each changed file in turn and re-running the whole test suite for each ' +
      'one, to prove every change is load-bearing. That is one full suite run per changed ' +
      'file, so several minutes is normal.'
  }
  if (phase === 'implementation') {
    return 'Applying the change and running the tests written for it.'
  }
  if (phase === 'test_authoring') {
    return 'Writing tests that must FAIL against the tree as it stands, before anything is ' +
      'changed.'
  }
  return null
}

export interface ElapsedReading {
  seconds: number
  /** True once the turn has run longer than a turn can legitimately take. */
  overBudget: boolean
  text: string
}

/** How long the in-flight turn has been running, against the budget that bounds one turn. */
export function turnElapsed(run: AuthoringRunState | null, now: Date): ElapsedReading | null {
  if (!run?.turn_in_flight || !run.turn_started_at) return null
  const started = Date.parse(run.turn_started_at)
  if (Number.isNaN(started)) return null
  const seconds = Math.max(0, Math.floor((now.getTime() - started) / 1000))
  const mins = Math.floor(seconds / 60)
  const budgetMins = Math.floor(TURN_BUDGET_SECONDS / 60)
  const overBudget = seconds > TURN_BUDGET_SECONDS
  // THE BUDGET BOUNDS THIS TURN, not the whole phase. Label both numbers so the phase
  // total beside this reading cannot be mistaken for a single model call.
  const text = overBudget
    ? `this turn ${mins} min — past the ${budgetMins} min limit a turn is allowed`
    : `this turn ${mins} min of a ${budgetMins} min limit`
  return { seconds, overBudget, text }
}

export interface RunGuidance {
  /** What went wrong, in the operator's terms. */
  headline: string
  /** What to actually do about it. Never empty — a diagnosis with no action is half an answer. */
  action: string
  /** Specific things to look at, when the verdict named any. */
  detail?: string[]
}

/** Pulls the `FAILING:`/`FROZEN` node ids a refusal listed, so guidance can name them. */
function namedTests(reason: string): string[] {
  const out: string[] = []
  for (const m of reason.matchAll(/([\w./-]+\.py::[\w[\]-]+)/g)) {
    if (!out.includes(m[1])) out.push(m[1])
  }
  return out.slice(0, 6)
}

/**
 * The file paths a refusal named, so guidance can show WHICH file is at fault.
 *
 * SEPARATE FROM namedTests, which requires a `::` node id. A declared file that was never
 * delivered has no node id — it is a path — and reusing the test matcher would return an
 * empty list, which is how a refusal that names exactly one thing renders as naming nothing.
 *
 * CUT AT THE CHANGESET SENTENCE. `declared_file_not_delivered` names the missing file first
 * and then, deliberately, lists what the changeset ALREADY holds so the turn stops re-emitting
 * it. Matching the whole reason would show the delivered files as the missing ones, which is
 * the same confusion of subject this whole change is about.
 *
 * BACKTICKS OPTIONAL. The backend quotes paths in the declared-file refusal and does not in
 * the collection-error line; requiring them matched one and silently missed the other.
 */
function namedPaths(reason: string): string[] {
  const head = reason.split('THE CHANGESET IS NOT EMPTY')[0]
  const out: string[] = []
  for (const m of head.matchAll(/([\w./-]+\.(?:py|yaml|yml|md|json))/g)) {
    if (!out.includes(m[1])) out.push(m[1])
  }
  return out.slice(0, 6)
}

/**
 * The guidance for a run that has stopped being able to help itself.
 *
 * Returns null while a run is progressing normally — guidance shown constantly is guidance
 * nobody reads.
 */
export function runGuidance(run: AuthoringRunState | null): RunGuidance | null {
  if (!run) return null
  if (runStoppedBy(run)) return null
  if (run.turn_in_flight || run.active_attempt) return null

  const verdicts = run.verdicts ?? []
  const last = verdicts[verdicts.length - 1]

  // THE RUN DELIVERED EVERYTHING EXCEPT THE FILE IT WAS ASKED FOR.
  //
  // Measured on analytics/daily-check-report run 20260916T132736Z-492af5: the operator's
  // request, both plan steps and the design all named `collect_clarity.py`, and twelve
  // attempts returned an unchanged 54,864-char workflow instead. The operator's question at
  // the end was "what would I click?" — so this says which button, and why.
  if (last && !last.passed && last.code === 'implementation.declared_file_not_delivered') {
    return {
      headline: 'The run has not delivered the file this change is about.',
      action: 'Resume — the refusal now names the missing file, which earlier attempts were ' +
        'never told. If it comes back a second time with the same file missing, Redrive ' +
        'implementation with a note saying the workflow YAML does not need to change.',
      detail: namedPaths(last.reason ?? ''),
    }
  }

  // AND A TEST THAT DID NOT RUN IS NOT A TEST THAT FAILED.
  //
  // pytest reports a test it could not import as a collection ERROR, and the run is told its
  // tests are still failing. An operator reading that looks at assertions; the fault is that
  // the file cannot be loaded at all. Most often the test imports an ADR-080 step body, whose
  // top-level `return` is correct in the engine and illegal to `importlib`.
  if (last && !last.passed && (last.reason ?? '').includes('DID NOT RUN')) {
    return {
      headline: 'The tests did not run — they could not be imported, so nothing was measured.',
      action: 'Redrive test authoring, not implementation. A test that imports a `python` ' +
        "step body cannot pass: the engine wraps that file in a function, so its top-level " +
        '`return` is correct there and a syntax error to `importlib`. Test the functions it ' +
        'imports, or assert on the activity YAML.',
      detail: namedPaths(last.reason ?? ''),
    }
  }

  // THE BACKEND ALREADY KNOWS THIS ONE. implementation.tests_may_be_unsatisfiable fires when
  // the same tests fail identically across attempts while the draft changes, and it routes to
  // test_authoring. Surfacing it is the difference between an operator rewriting their code
  // again and an operator reading two tests.
  if (last && !last.passed && last.code === 'implementation.tests_may_be_unsatisfiable') {
    return {
      headline: 'These tests may be impossible to satisfy — the implementation is probably fine.',
      action: 'Open the tests below and check they can pass for ANY correct version of this ' +
        'workflow. The usual fault is an assertion reading the wrong nesting level. Fix the ' +
        'TEST, then Resume.',
      detail: namedTests(last.reason ?? ''),
    }
  }

  if (last && !last.passed && last.code === 'implementation.tests_unmeasured') {
    if ((last.reason ?? '').includes('ran out of time (job deadline)')) {
      return {
        headline: 'The turn ran out of time before the tests could be measured.',
        action: 'Nothing is wrong with the change or the environment — do not rewrite it. ' +
          'Resume the run; the next attempt measures it with a fresh time budget.',
      }
    }
    const reason = last.reason ?? ''
    if (reason.includes('the gate ran past its own time limit')) {
      const m = reason.match(/did not finish within (\S+) and was stopped after (\S+) /)
      return {
        headline: m
          ? `The fleet's gate ran past its ${m[1]} time limit (stopped after ${m[2]}) before reporting a verdict, so nothing was measured.`
          : "The fleet's gate ran past its own time limit before reporting a verdict, so nothing was measured.",
        action: 'This is not a verdict on the change. A timeout has two usual causes: the fleet\'s ' +
          'test suite needs longer than the limit on this host (gate capacity), or a test or the ' +
          'implementation hangs. Read what the gate printed and check whether the change can loop, ' +
          'wait or block before rewriting anything. If it cannot, Resume to measure again; if it ' +
          'keeps hitting the limit, the suite needs to run faster or the host needs more capacity.',
      }
    }
    return {
      headline: 'The test runner could not run, so nothing was measured about this change.',
      action: 'This is an environment fault, not a fault in the work — do not rewrite it. ' +
        'Check the fleet test runner, then Resume.',
    }
  }

  // A BUDGET SPENT ON REFUSALS, with the frozen shape underneath it. The run cannot tell the
  // operator this itself once it has stopped, so the exit reason is read here.
  const exit = run.redrive_exit ?? ''
  if (exit.includes('every attempt was refused')) {
    const failing = verdicts.filter(v => !v.passed && v.code === 'implementation.tests_still_failing')
    if (failing.length >= 3) {
      const names = namedTests(failing[failing.length - 1].reason ?? '')
      return {
        headline: 'This run spent its whole budget on the same failing tests.',
        action: 'When failures do not change as the implementation changes, the tests are the ' +
          'suspect. Read the ones below before changing the workflow again — then Redrive ' +
          'test authoring, not implementation.',
        detail: names,
      }
    }
    return {
      headline: 'This run used every attempt it had and stopped.',
      action: 'Read the last refusal below. If it names something you disagree with, Redrive ' +
        'that phase with a note saying why.',
    }
  }

  return null
}
